import { carimboDaMensagem, type CanalDetalhe } from '@mymaxx/shared';
import { VisualizadorMidia } from '@mymaxx/ui';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Hash, Image as Icone, Star, Users } from 'lucide-react';
import { useState } from 'react';
import { internoApi } from '../lib/interno-api';

/**
 * O perfil de quem está do outro lado — ou do grupo.
 *
 * Não existia neste app: o técnico via o nome no alto do fio e nada mais. Para saber quem
 * é a pessoa, de que setor, ou quem está num grupo de doze, ele tinha que perguntar.
 *
 * Tela inteira porque é um lugar para onde se vai e de onde se volta, e porque no celular
 * uma gaveta lateral com foto, lista de membros e galeria não cabe sem virar rolagem
 * dentro de rolagem.
 */
export function PerfilDaConversa({
  canal,
  onVoltar,
}: {
  canal: CanalDetalhe;
  onVoltar: () => void;
}) {
  const detalhes = useQuery({
    queryKey: ['interno-detalhes', canal.id],
    queryFn: () => internoApi.detalhes(canal.id),
  });
  const [ampliada, setAmpliada] = useState<string | null>(null);

  const outro = canal.comQuem;
  const presentes = canal.membros.filter((m) => !m.saiu);
  const foto = canal.tipo === 'grupo' ? canal.avatarUrl : outro?.avatarUrl;
  const nome = canal.nome;

  return (
    <main className="flex min-h-dvh flex-col bg-neutral-50 dark:bg-neutral-950">
      <header className="flex items-center gap-3 border-b border-neutral-200 bg-white px-4 py-3 dark:border-neutral-800 dark:bg-neutral-900">
        <button type="button" onClick={onVoltar} aria-label="Voltar" className="text-neutral-500">
          <ArrowLeft size={20} />
        </button>
        <p className="truncate text-sm font-medium">
          {canal.tipo === 'grupo' ? 'Dados do grupo' : 'Dados do contato'}
        </p>
      </header>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <section className="flex flex-col items-center gap-2 rounded-card bg-white p-5 text-center dark:bg-neutral-900">
          {foto ? (
            <button
              type="button"
              onClick={() => setAmpliada(foto)}
              aria-label="Ver foto"
              className="size-24 overflow-hidden rounded-full"
            >
              <img src={foto} alt="" className="size-full object-cover" />
            </button>
          ) : (
            <span className="flex size-24 items-center justify-center rounded-full bg-neutral-200 text-2xl font-medium text-neutral-500 dark:bg-neutral-700 dark:text-neutral-200">
              {canal.tipo === 'grupo' ? <Users size={32} /> : iniciais(nome)}
            </span>
          )}
          <h1 className="text-lg font-semibold">{nome}</h1>
          {canal.tipo === 'grupo' ? (
            <p className="text-xs text-neutral-500">
              {presentes.length} participante{presentes.length === 1 ? '' : 's'}
            </p>
          ) : (
            <p className="text-xs text-neutral-500">
              {outro?.online ? 'Disponível agora' : estadoDe(outro?.vistoEm ?? null)}
            </p>
          )}
          {canal.descricao && (
            <p className="text-sm whitespace-pre-line text-neutral-600 dark:text-neutral-300">
              {canal.descricao}
            </p>
          )}
        </section>

        {canal.tipo === 'grupo' && (
          <Secao titulo={`Participantes (${presentes.length})`} icone={<Users size={14} />}>
            <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {presentes.map((m) => (
                <li key={`${m.tipo}:${m.id}`} className="flex items-center gap-3 px-3 py-2 text-sm">
                  {m.avatarUrl ? (
                    <img src={m.avatarUrl} alt="" className="size-8 rounded-full object-cover" />
                  ) : (
                    <span className="flex size-8 items-center justify-center rounded-full bg-neutral-200 text-[11px] font-medium text-neutral-600 dark:bg-neutral-700 dark:text-neutral-200">
                      {iniciais(m.nome ?? '?')}
                    </span>
                  )}
                  <span className="min-w-0 flex-1 truncate">{m.nome}</span>
                  {m.papel !== 'membro' && (
                    <span className="shrink-0 rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                      {m.papel === 'dono' ? 'Dono' : 'Admin'}
                    </span>
                  )}
                  {m.online && <span className="size-2 shrink-0 rounded-full bg-emerald-500" />}
                </li>
              ))}
            </ul>
          </Secao>
        )}

        <Secao titulo={`Mídia (${detalhes.data?.midias.length ?? 0})`} icone={<Icone size={14} />}>
          {detalhes.isPending && <p className="px-3 py-3 text-xs text-neutral-500">Carregando…</p>}
          {detalhes.isSuccess && detalhes.data.midias.length === 0 && (
            <p className="px-3 py-3 text-xs text-neutral-500">Nada enviado nesta conversa ainda.</p>
          )}
          {!!detalhes.data?.midias.length && (
            <div className="grid grid-cols-3 gap-1 p-2">
              {detalhes.data.midias.slice(0, 12).map((m) =>
                m.midia?.url && m.tipo === 'imagem' ? (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setAmpliada(m.midia!.url)}
                    className="aspect-square overflow-hidden rounded"
                  >
                    <img src={m.midia.url} alt="" className="size-full object-cover" />
                  </button>
                ) : (
                  <span
                    key={m.id}
                    className="flex aspect-square items-center justify-center rounded bg-neutral-100 p-1 text-center text-[10px] break-all text-neutral-500 dark:bg-neutral-800"
                  >
                    {m.midia?.nomeOriginal ?? 'Arquivo'}
                  </span>
                ),
              )}
            </div>
          )}
        </Secao>

        <Secao
          titulo={`Mensagens guardadas (${detalhes.data?.guardadas.length ?? 0})`}
          icone={<Star size={14} />}
        >
          {detalhes.data?.guardadas.length === 0 && (
            <p className="px-3 py-3 text-xs text-neutral-500">
              Nada guardado. Use a estrela na mensagem para marcar o que vai precisar achar depois.
            </p>
          )}
          <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
            {(detalhes.data?.guardadas ?? []).map((m) => (
              <li key={m.id} className="px-3 py-2 text-xs">
                <p className="line-clamp-2 text-neutral-700 dark:text-neutral-300">
                  {m.texto ?? 'Anexo'}
                </p>
                <p className="mt-0.5 text-[11px] text-neutral-400">
                  {carimboDaMensagem(m.criadaEm)}
                </p>
              </li>
            ))}
          </ul>
        </Secao>

        {canal.tipo === 'direto' && !!detalhes.data?.gruposEmComum.length && (
          <Secao
            titulo={`Grupos em comum (${detalhes.data.gruposEmComum.length})`}
            icone={<Hash size={14} />}
          >
            <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {detalhes.data.gruposEmComum.map((g) => (
                <li key={g.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                  {g.avatarUrl ? (
                    <img src={g.avatarUrl} alt="" className="size-7 rounded-full object-cover" />
                  ) : (
                    <span className="flex size-7 items-center justify-center rounded-full bg-neutral-200 dark:bg-neutral-700">
                      <Hash size={12} className="text-neutral-500" />
                    </span>
                  )}
                  <span className="truncate">{g.nome}</span>
                </li>
              ))}
            </ul>
          </Secao>
        )}
      </div>

      {ampliada && (
        <VisualizadorMidia
          url={ampliada}
          nomeOriginal={nome}
          mimeType="image/*"
          perguntarOndeSalvar
          aoFechar={() => setAmpliada(null)}
        />
      )}
    </main>
  );
}

function Secao({
  titulo,
  icone,
  children,
}: {
  titulo: string;
  icone: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-card bg-white dark:bg-neutral-900">
      <h2 className="flex items-center gap-2 border-b border-neutral-200 px-3 py-2 text-xs font-medium text-neutral-500 dark:border-neutral-800">
        {icone}
        {titulo}
      </h2>
      {children}
    </section>
  );
}

/** As duas primeiras letras do nome, para quem não tem foto. */
function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  return ((partes[0]?.[0] ?? '') + (partes[1]?.[0] ?? '')).toUpperCase() || '?';
}

/** "Visto por último" em palavras, ou o silêncio de quem não compartilha. */
function estadoDe(vistoEm: string | null): string {
  if (!vistoEm) return 'Sem status';
  return `Visto por último em ${carimboDaMensagem(vistoEm)}`;
}
